<template>
  <div class="user-avatar-badge" :style="wrapperStyle">
    <q-avatar :size="size" :style="avatarStyle">
      <template v-if="currentSrc">
        <q-img
          :key="currentSrc"
          :src="currentSrc"
          :ratio="1"
          :img-style="{ objectFit: 'cover' }"
          referrerpolicy="no-referrer"
          loading="lazy"
          @error="onImgError"
          :alt="displayName || username || email || 'User avatar'"
        />
      </template>
      <template v-else>
        {{ userInitial }}
      </template>
    </q-avatar>
    <PlanBadge v-if="showPlan" :plan="planObj.value.id" class="user-avatar-plan-badge" />
  </div>
</template>

<script setup>
  import PlanBadge from '@/components/ui/badges/PlanBadge.vue'
  import { computed, onMounted, ref, watch } from 'vue'
  import { usePlansStore } from '@/stores/plansStore'
  import { useUserStore } from '@/stores/userStore'

  const props = defineProps({
    avatar: String,
    photoURL: String, // allow photoURL as prop
    displayName: String,
    username: String,
    uid: String,
    email: String,
    plan: String,
    size: {
      type: String,
      default: '32px'
    },
    showPlan: {
      type: Boolean,
      default: false
    },
    style: {
      type: Object,
      default: () => ({})
    }
  })

  const userStore = useUserStore()
  const plansStore = usePlansStore()
  onMounted(() => {
    if (!plansStore.plans?.length) plansStore.fetchPlans()
  })
  const planIndex = computed(() =>
    Object.fromEntries((plansStore.plans || []).map((p) => [p.id, p]))
  )

  // Determine if user-identifying props are present
  const isPropUser = computed(
    () =>
      props.uid ||
      props.email ||
      props.displayName ||
      props.username ||
      props.avatar ||
      props.photoURL ||
      props.plan
  )

  const avatar = computed(() => {
    if (isPropUser.value) {
      return props.avatar || props.photoURL || undefined
    } else {
      return userStore.profile?.avatar || userStore.profile?.photoURL || undefined
    }
  })

  // Harden remote Google avatar loading by trying a couple of size variants and using no-referrer
  const isGoogleLh3 = (url) =>
    typeof url === 'string' && /^https?:\/\/lh\d+\.googleusercontent\.com\/a\//i.test(url)
  const normalizeGoogleAvatar = (url, size = 256) => {
    try {
      if (!url) return url
      if (/=s\d+-c/i.test(url)) {
        return url.replace(/=s\d+-c/i, `=s${size}-c`)
      }
      const u = new URL(url)
      // Many variants also accept ?sz=
      u.searchParams.set('sz', String(size))
      return u.toString()
    } catch {
      return url
    }
  }

  const candidates = computed(() => {
    const urls = []
    const raw = avatar.value
    if (raw) {
      if (isGoogleLh3(raw)) {
        urls.push(normalizeGoogleAvatar(raw, 256))
        urls.push(normalizeGoogleAvatar(raw, 128))
      }
      urls.push(raw)
    }
    return urls
  })

  const idx = ref(0)
  const currentSrc = computed(() => candidates.value[idx.value] || '')

  watch(avatar, () => {
    idx.value = 0
  })

  function onImgError() {
    const failed = candidates.value[idx.value]
    // Keep existing global error logging behavior
    try {
      console.error('UserAvatarBadge: failed to load avatar', failed)
    } catch {
      /* ignore */
    }
    if (idx.value < candidates.value.length - 1) idx.value++
    else idx.value = candidates.value.length // exhaust -> fallback to initials
  }

  const displayName = computed(() => {
    if (isPropUser.value) {
      return props.displayName
    } else {
      return userStore.profile?.displayName
    }
  })

  const username = computed(() => {
    if (isPropUser.value) {
      return props.username
    } else {
      return userStore.profile?.username
    }
  })

  const email = computed(() => {
    if (isPropUser.value) {
      return props.email
    } else {
      return userStore.profile?.email
    }
  })

  const plan = computed(() => {
    if (isPropUser.value) {
      return props.plan || 'free'
    } else {
      return userStore.profile?.plan || 'free'
    }
  })

  const planObj = computed(() => planIndex.value[plan.value || 'free'] || {})

  const userInitial = computed(() => {
    let name = displayName.value || username.value || ''
    if (!name && email.value) {
      name = email.value.split('@')[0]
    }
    if (!name || typeof name !== 'string' || name.length === 0) return '?'
    const parts = name.trim().split(/\s+/)
    if (parts.length >= 2) {
      const first = parts[0].charAt(0)
      const last = parts[parts.length - 1].charAt(0)
      return (first + last).toUpperCase()
    }
    return name.charAt(0).toUpperCase()
  })

  const avatarStyle = computed(() => {
    const color = planObj.value.color || 'transparent'
    return {
      outline: `2px solid ${color}`,
      background: `radial-gradient(circle, #888 0%, ${color} 100%)`
    }
  })

  const wrapperStyle = computed(() => ({
    position: 'relative',
    display: 'inline-block',
    ...props.style
  }))
</script>
